import { parseUnits } from 'viem'
import { toGenericPriceKey, toOracleKey } from '../../assets'
import { GenericTokenList } from '../../types'
import { SILO_API_SUPPORTED_CHAIN_IDS } from '../../lending/public-data/silo-v2/fetchPublic'
import { composeVaultDisplayName } from '../displayName'
import { curatorNameFromVaultName } from '../curatorName'
import { VaultMarketExposure, withIdleExposure } from '../exposure'
import { deriveConvertToAssets } from '../sharePrice'
import { SiloVault, SiloVaultReward, SiloVaults } from './types'

const SILO_API_URL = 'https://api-v3.silo.finance'

/**
 * Single multi-root query: vaults + their per-market allocation positions
 * (`positions` with `isVaultAllocation: true`) + market metadata + the
 * incentive programs paying vault depositors. One round trip; the roots are
 * joined client-side to build each vault's `exposures`, `rewards` and
 * utilization.
 *
 * Why `positions` over `vaultAllocationViews`: both expose the market's
 * *total* collateral in `assets`/`collateralAssets`, not the vault's slice.
 * Only `position.sTokenBalance` (the vault's collateral shares) lets us
 * reconstruct the vault's actual size via the market's share/asset ratio.
 *
 * `incentivesPrograms` is keyed by `shareTokenId` — the token the program
 * pays holders OF. When that token is a vault address the program is a
 * vault-level reward; when it is a silo share token it belongs to the
 * lending side and is none of this fetcher's business.
 */
const vaultsQuery = (chainId: string, limit: number) => `
query GetSiloVaults {
  vaults(where: {chainId_in: [${chainId}]}, limit: ${limit}) {
    items {
      id
      chainId
      name
      symbol
      decimals
      protocolId
      protocol { id protocolVersion }
      asset { id symbol decimals }
      ownerId
      curatorId
      guardianId
      allocators
      feeRecipient
      totalAssets
      totalAssetsUsd
      totalSupply
      performanceFee
      timelock
      apr
      userApr
    }
  }
  positions(where: {chainId_in: [${chainId}], isVaultAllocation: true, isOpen: true}, limit: ${limit}) {
    items {
      accountId
      marketId
      sTokenBalance
    }
  }
  markets(where: {chainId_in: [${chainId}]}, limit: ${limit}) {
    items {
      id
      depositRate
      collateralSharesSupply
      collateralAssetsSupply
      liquidity
      utilization
      otherMarket { inputToken { id } }
    }
  }
  incentivesPrograms(where: {chainId_in: [${chainId}]}, limit: ${limit}) {
    items {
      id
      name
      shareTokenId
      emissionPerSecond
      distributionEnd
      apr
      rewardToken { id symbol decimals }
    }
  }
}
`

interface ApiVaultItem {
  id: string
  chainId: number
  name: string | null
  symbol: string | null
  decimals: number | null
  protocolId: string | null
  protocol: { id: string; protocolVersion: string } | null
  asset: { id: string; symbol: string | null; decimals: number | null } | null
  ownerId: string | null
  curatorId: string | null
  guardianId: string | null
  allocators: string[] | null
  feeRecipient: string | null
  totalAssets: string | null
  totalAssetsUsd: number | null
  totalSupply: string | null
  performanceFee: string | null
  timelock: string | number | null
  apr: string | number | null
  userApr: string | number | null
}

interface ApiPositionItem {
  accountId: string | null
  marketId: string | null
  sTokenBalance: string | number | null
}

interface ApiMarketItem {
  id: string | null
  depositRate: string | number | null
  collateralSharesSupply: string | number | null
  collateralAssetsSupply: string | number | null
  /** Liquid (currently-withdrawable) collateral assets in the market —
   *  `supplied − borrowed`, decimal-formatted in the asset's units. */
  liquidity: string | number | null
  /** Borrowed share of the market, as a FRACTION in `0..1` — verified live
   *  to equal `1 − liquidity / collateralAssetsSupply` exactly. */
  utilization: string | number | null
  otherMarket: { inputToken: { id: string | null } | null } | null
}

interface ApiIncentivesProgramItem {
  id: string | null
  name: string | null
  /** The token whose holders this program pays. Equals a vault address for
   *  vault-level rewards. */
  shareTokenId: string | null
  emissionPerSecond: string | number | null
  distributionEnd: string | number | null
  /** The indexer's own APR figure. UNIT UNVERIFIED — see
   *  `SiloVaultReward.indexerApr`. */
  apr: string | number | null
  rewardToken: {
    id: string | null
    symbol: string | null
    decimals: number | null
  } | null
}

/**
 * marketId (lowercased) → collateral token, supply APR, and the share/asset
 * ratio needed to turn a vault's `sTokenBalance` into an asset amount.
 */
type MarketMeta = {
  collateralAddress: string
  supplyApr: number
  collateralShares: number
  collateralAssets: number
  /** Liquid (withdrawable) assets in the market, underlying units. */
  liquidity: number
  /** Borrowed fraction of the market, `0..1`. */
  utilization: number
}

/**
 * Build each vault's `exposures` from its allocation positions joined to
 * market metadata. The vault's per-market size is reconstructed from its
 * collateral shares: `assets = sTokenBalance * collateralAssets /
 * collateralShares`. Weighting is over `totalAssetsFormatted` (same token,
 * same snapshot) so idle balances dilute the weights.
 *
 * A vault cannot hold more in a single market than its entire balance, so
 * any reconstructed amount exceeding total assets is a stale/corrupt indexer
 * row and is dropped. Remaining rows are sorted by weight descending.
 */
function buildExposures(
  vaultId: string,
  totalAssetsFormatted: number,
  pricePerUnit: number,
  positionsByVault: Map<string, ApiPositionItem[]>,
  marketMeta: Map<string, MarketMeta>,
  tokenList: GenericTokenList,
): VaultMarketExposure[] | undefined {
  if (totalAssetsFormatted <= 0) return undefined
  const rows = positionsByVault.get(vaultId) ?? []

  const exposures: VaultMarketExposure[] = []
  for (const row of rows) {
    const marketId = (row.marketId ?? '').toLowerCase()
    if (!marketId) continue
    const meta = marketMeta.get(marketId)
    if (!meta || meta.collateralShares <= 0) continue
    const shares = Number(row.sTokenBalance ?? 0)
    if (!Number.isFinite(shares) || shares <= 0) continue

    const assets = (shares * meta.collateralAssets) / meta.collateralShares
    // Physical invariant: a market slice can't exceed the vault's total.
    // A small epsilon absorbs rounding between the share and asset feeds.
    if (!(assets > 0) || assets > totalAssetsFormatted * 1.01) continue

    exposures.push({
      marketId,
      collateralAddress: meta.collateralAddress,
      collateral: meta.collateralAddress
        ? tokenList[meta.collateralAddress]
        : undefined,
      assets,
      assetsUsd: assets * pricePerUnit,
      weightPct: (assets / totalAssetsFormatted) * 100,
      supplyApr: meta.supplyApr,
    })
  }
  exposures.sort((a, b) => b.weightPct - a.weightPct)
  // Tag uninvested deposits as an idle entry so the breakdown sums to ~100%.
  const withIdle = withIdleExposure(
    exposures,
    totalAssetsFormatted,
    pricePerUnit,
  )
  return withIdle.length > 0 ? withIdle : undefined
}

/**
 * Real immediately-withdrawable liquidity for a vault, formatted in
 * underlying units, plus whether that figure was actually PROVEN.
 *
 * The holder of 100% of shares can pull, from each allocation, at most the
 * market's liquid cash (`min(allocation, market.liquidity)`), plus all
 * uninvested idle deposits. Clamped to `totalAssets`.
 *
 * `proven` is the load-bearing half. With no resolvable allocation rows the
 * arithmetic treats the whole vault as idle and returns `totalAssets` — which
 * is indistinguishable from a genuinely fully-liquid vault, and the term-sheet
 * builder reads `liquidity >= totalAssets` as proof of a same-block exit
 * (`exit.mode: 'instant'`). A vault whose allocations the indexer simply did
 * not return would then advertise an unconditional withdrawal it cannot
 * honour. So an unproven figure is reported as such and the caller pins
 * `withdrawalMode: 'instant-capped'` instead of letting the builder infer.
 *
 * A vault with no allocations AND no assets is not unproven — there is
 * nothing to be wrong about — so it stays `proven`.
 */
function computeSiloLiquidityFormatted(
  vaultId: string,
  totalAssetsFormatted: number,
  positionsByVault: Map<string, ApiPositionItem[]>,
  marketMeta: Map<string, MarketMeta>,
): { formatted: number; proven: boolean } {
  if (totalAssetsFormatted <= 0) return { formatted: 0, proven: true }
  const rows = positionsByVault.get(vaultId) ?? []
  let allocated = 0
  let withdrawable = 0
  let resolved = 0
  for (const row of rows) {
    const marketId = (row.marketId ?? '').toLowerCase()
    if (!marketId) continue
    const meta = marketMeta.get(marketId)
    if (!meta || meta.collateralShares <= 0) continue
    const shares = Number(row.sTokenBalance ?? 0)
    if (!Number.isFinite(shares) || shares <= 0) continue
    const assets = (shares * meta.collateralAssets) / meta.collateralShares
    if (!(assets > 0) || assets > totalAssetsFormatted * 1.01) continue
    resolved += 1
    allocated += assets
    // The vault can pull at most the market's liquid cash from this slice.
    withdrawable += Math.min(assets, meta.liquidity)
  }
  // Uninvested (idle) deposits are always instantly withdrawable.
  const idle = Math.max(0, totalAssetsFormatted - allocated)
  return {
    formatted: Math.min(totalAssetsFormatted, idle + withdrawable),
    proven: resolved > 0,
  }
}

/**
 * The share of a vault's assets that is lent out, as a fraction in `0..1` —
 * the allocation-weighted utilization of the silos it is deployed into, with
 * idle assets counted at zero because uninvested cash is not lent.
 *
 * This is a DIFFERENT question from `liquidity`, and the two must not be
 * derived from each other. Liquidity asks "how much can I pull right now",
 * and is capped per market at that market's cash (`min(allocation, cash)`).
 * Utilization asks "how much of my money is working", and is pro-rata
 * (`allocation × marketUtilization`) — a vault holding 10 in a market with
 * 1000 of cash at 90% utilization is fully withdrawable AND 90% lent.
 *
 * Returns `undefined` when nothing resolves, so the term sheet reports the
 * block as pending rather than claiming an idle vault.
 */
function computeSiloUtilization(
  vaultId: string,
  totalAssetsFormatted: number,
  positionsByVault: Map<string, ApiPositionItem[]>,
  marketMeta: Map<string, MarketMeta>,
): { borrowed: number; total: number } | undefined {
  if (totalAssetsFormatted <= 0) return undefined
  const rows = positionsByVault.get(vaultId) ?? []
  let borrowed = 0
  let resolved = 0
  for (const row of rows) {
    const marketId = (row.marketId ?? '').toLowerCase()
    if (!marketId) continue
    const meta = marketMeta.get(marketId)
    if (!meta || meta.collateralShares <= 0) continue
    const shares = Number(row.sTokenBalance ?? 0)
    if (!Number.isFinite(shares) || shares <= 0) continue
    const assets = (shares * meta.collateralAssets) / meta.collateralShares
    if (!(assets > 0) || assets > totalAssetsFormatted * 1.01) continue
    resolved += 1
    borrowed += assets * clamp01(meta.utilization)
  }
  if (resolved === 0) return undefined
  return {
    borrowed: Math.min(borrowed, totalAssetsFormatted),
    total: totalAssetsFormatted,
  }
}

const clamp01 = (n: number): number =>
  !Number.isFinite(n) ? 0 : n < 0 ? 0 : n > 1 ? 1 : n

/**
 * Performance-fee scaling constant. Silo stores `performanceFee` as a
 * 1e18-scaled fraction (e.g. `5e17` = 50 %). Divide by this to get percent.
 */
const PERFORMANCE_FEE_SCALE = 1e16

const SECONDS_PER_YEAR = 31_536_000

/**
 * Live incentive programs paying a vault's depositors, priced into an APR.
 *
 * **Why the APR is computed here rather than read from `program.apr`.** The
 * indexer publishes its own `apr`, but its unit is unverifiable today: every
 * program in the Silo book is expired or emits zero, so there is no live
 * figure to reconcile against, and the two candidate readings — percent like
 * `vault.apr`, or a fraction like `market.utilization` — differ by 100×. The
 * repo has shipped that exact class of bug before (`aave-earn` reporting a
 * fee as a fraction while five siblings used percent), and a rewards leg
 * wrong by 100× would put a vault at the top of the earn leaderboard. So the
 * headline number is derived from quantities whose units are unambiguous —
 * emission rate, token decimals, price, TVL — and the indexer's figure is
 * carried verbatim as `indexerApr` purely so the first live program can
 * settle the question.
 *
 * A program whose reward token we cannot price is REPORTED but not counted:
 * it appears in `rewards` with no `apr`, and `priced` goes false so the
 * caller can mark the rate incomplete. Dropping it silently would understate
 * the yield; guessing a price would invent one.
 */
function buildVaultRewards(
  vaultId: string,
  totalAssetsUsd: number,
  programsByShareToken: Map<string, ApiIncentivesProgramItem[]>,
  priceOf: (address: string) => number,
  nowSeconds: number,
): { rewards?: SiloVaultReward[]; rewardsRate: number; priced: boolean } {
  const programs = programsByShareToken.get(vaultId) ?? []
  const rewards: SiloVaultReward[] = []
  let rewardsRate = 0
  let priced = true

  for (const p of programs) {
    const emission = Number(p.emissionPerSecond ?? 0)
    const endsAt = Number(p.distributionEnd ?? 0)
    // A program that has stopped emitting, or whose window has closed, pays
    // nothing today. `distributionEnd === 0` is the indexer's "never ran".
    if (!Number.isFinite(emission) || emission <= 0) continue
    if (!Number.isFinite(endsAt) || endsAt <= nowSeconds) continue

    const tokenAddress = (p.rewardToken?.id ?? '').toLowerCase()
    const tokenDecimals = Number(p.rewardToken?.decimals ?? 18)
    const tokenPrice = tokenAddress ? priceOf(tokenAddress) : 0

    let apr: number | undefined
    if (tokenPrice > 0 && totalAssetsUsd > 0) {
      const annualTokens = (emission / 10 ** tokenDecimals) * SECONDS_PER_YEAR
      apr = ((annualTokens * tokenPrice) / totalAssetsUsd) * 100
      rewardsRate += apr
    } else {
      priced = false
    }

    rewards.push({
      programId: p.id ?? '',
      name: p.name ?? undefined,
      tokenAddress,
      tokenSymbol: p.rewardToken?.symbol ?? undefined,
      tokenDecimals,
      emissionPerSecond: String(p.emissionPerSecond ?? '0'),
      endsAt,
      apr,
      indexerApr: p.apr == null ? undefined : Number(p.apr),
    })
  }

  return {
    rewards: rewards.length > 0 ? rewards : undefined,
    rewardsRate,
    priced,
  }
}

const toRawAmount = (
  decimalString: string | null,
  decimals: number,
): string => {
  if (!decimalString) return '0'
  try {
    // `parseUnits` accepts a decimal string and returns wei-scale bigint.
    // The Silo indexer emits fixed-decimal strings like `7370856.39318`.
    return parseUnits(decimalString, decimals).toString()
  } catch {
    return '0'
  }
}

/** Everything reconstructed from the sibling query roots for one vault. */
type DerivedVaultFacts = {
  exposures: VaultMarketExposure[] | undefined
  liquidityFormatted: number
  /** False when no allocation row resolved — see `computeSiloLiquidityFormatted`. */
  liquidityProven: boolean
  utilization: { borrowed: number; total: number } | undefined
  rewards: SiloVaultReward[] | undefined
  rewardsRate: number
  rewardsPriced: boolean
}

function parseVault(
  v: ApiVaultItem,
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
  derived: DerivedVaultFacts,
): SiloVault | null {
  const {
    exposures,
    liquidityFormatted,
    liquidityProven,
    utilization,
    rewards,
    rewardsRate,
    rewardsPriced,
  } = derived
  const address = (v?.id ?? '').toLowerCase()
  const assetAddr = (v?.asset?.id ?? '').toLowerCase()
  if (!address || !assetAddr) return null

  const assetMeta = tokenList[assetAddr]
  const decimals = Number(
    v.decimals ?? v.asset?.decimals ?? assetMeta?.decimals ?? 18,
  )

  const totalAssetsFormatted = Number(v.totalAssets ?? 0) || 0
  const totalAssetsRaw = toRawAmount(v.totalAssets, decimals)
  const totalSupplyRaw = toRawAmount(v.totalSupply, decimals)
  // Silo share tokens share the asset's decimals (`decimals` re-encodes both
  // totals), so the same value is the share unit for the price.
  const convertToAssets = deriveConvertToAssets(
    totalAssetsRaw,
    totalSupplyRaw,
    decimals,
  )

  const oracleKey =
    toOracleKey(assetMeta?.assetGroup) ?? toGenericPriceKey(assetAddr, chainId)
  const priceUsd = prices[oracleKey] ?? prices[assetAddr] ?? 0

  // `apr` is the pre-fee base rate and `userApr` is the same number net of
  // the performance fee — verified live across the whole book:
  // `userApr === apr × (1 − performanceFee)` holds to float precision on
  // 16/16 vaults. Neither carries an incentive leg, contrary to what this
  // file and the vaults README asserted for months. That mattered: with
  // `rewardsRate` pinned at 0 on the assumption that rewards were already
  // folded in, an incentivised Silo vault would have reported its entire
  // campaign APR as protocol interest, and the earn row's rewards badge
  // would have stayed empty while a Morpho row beside it showed the split.
  // Rewards are a separate query root and are summed into `depositRate`.
  // Both fields are already percent.
  const supplyRate = Number(v.userApr ?? 0) || 0
  const grossRate = Number(v.apr ?? 0) || 0

  const feeRaw = Number(v.performanceFee ?? 0)
  const fee = Number.isFinite(feeRaw) ? feeRaw / PERFORMANCE_FEE_SCALE : 0

  const apiTotalAssetsUsd = Number(v.totalAssetsUsd ?? 0) || 0
  const totalAssetsUsd = apiTotalAssetsUsd || totalAssetsFormatted * priceUsd

  // The indexer names only the curator ADDRESS (`curatorId`); the human name
  // is derivable solely from the vault's own name — "Turtle USDC", "Gami
  // USDC" — the same situation (and the same heuristic) as Euler-Earn and
  // Lista.
  const curatorName = curatorNameFromVaultName(v.name, assetMeta?.symbol)

  return {
    address,
    underlying: assetAddr,
    symbol: v.symbol ?? '',
    name: v.name ?? '',
    displayName: composeVaultDisplayName(
      'Silo',
      curatorName,
      assetMeta,
      v.name ?? undefined,
    ),
    curatorName,
    decimals,
    protocolVersion: v.protocol?.protocolVersion ?? '',
    protocolId: (v.protocolId ?? v.protocol?.id ?? '').toLowerCase(),
    totalAssets: totalAssetsRaw,
    totalSupply: totalSupplyRaw,
    convertToAssets,
    supplyRate,
    grossRate,
    rewardsRate,
    depositRate: supplyRate + rewardsRate,
    rewards,
    rewardsIncomplete: rewardsPriced ? undefined : true,
    fee,
    timelock: Number(v.timelock ?? 0),
    owner: v.ownerId?.toLowerCase() || undefined,
    curator: v.curatorId?.toLowerCase() || undefined,
    guardian: v.guardianId?.toLowerCase() || undefined,
    // An allocator can move the vault's money between silos with NO timelock
    // — the one curation power that changes a depositor's exposure between
    // blocks. Carried so governance can state it; `timelock` alone overstates
    // how much notice a holder actually gets.
    allocators:
      v.allocators && v.allocators.length > 0
        ? v.allocators.map((a) => a.toLowerCase())
        : undefined,
    feeRecipient: v.feeRecipient?.toLowerCase() || undefined,
    asset: assetMeta,
    priceUsd: priceUsd || undefined,
    totalAssetsFormatted,
    totalAssetsUsd,
    // Real immediately-withdrawable liquidity: idle + Σ min(allocation,
    // market.liquidity) across the vault's silos (see
    // `computeSiloLiquidityFormatted`). Degrades to `totalAssets` only when
    // allocation data is absent. When fully liquid, reuse the exact
    // `totalAssetsRaw` to avoid float round-trip drift.
    liquidity:
      liquidityFormatted >= totalAssetsFormatted
        ? totalAssetsRaw
        : toRawAmount(liquidityFormatted.toFixed(decimals), decimals),
    liquidityFormatted,
    liquidityUsd:
      totalAssetsFormatted > 0
        ? liquidityFormatted * (totalAssetsUsd / totalAssetsFormatted)
        : 0,
    // Only claim a same-block exit when the allocation walk actually proved
    // one. Left undefined on a proven figure so the term-sheet builder makes
    // the `instant` / `instant-capped` call from the liquidity it can see.
    withdrawalMode: liquidityProven ? undefined : 'instant-capped',
    totalBorrowed:
      utilization &&
      toRawAmount(utilization.borrowed.toFixed(decimals), decimals),
    expectedLiquidity:
      utilization && toRawAmount(utilization.total.toFixed(decimals), decimals),
    exposures,
  }
}

/**
 * One-shot fetcher for all Silo v2/v3 vaults on a chain via the public
 * `api-v3.silo.finance` GraphQL indexer.
 *
 * The same endpoint serves both protocol versions — pass
 * `protocolVersion` to narrow to just one. Returns `{}` for any chain not
 * in `SILO_API_SUPPORTED_CHAIN_IDS` (Ethereum, Arbitrum, Avalanche, XDC,
 * Injective at time of writing). Silo vaults on Sonic must still be
 * fetched via a dedicated path when the indexer adds coverage.
 *
 * Unlike `fetchFluidFTokens` / `fetchGearboxV3Pools`, no multicall
 * executor is needed — the source is HTTP GraphQL.
 */
export const fetchSiloVaults = async (
  chainId: string,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
  options?: { protocolVersion?: 'v2' | 'v3'; limit?: number },
): Promise<SiloVaults> => {
  if (!SILO_API_SUPPORTED_CHAIN_IDS.has(chainId)) return {}

  const limit = options?.limit ?? 500
  const response = await fetch(SILO_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: vaultsQuery(chainId, limit) }),
  })
  if (!response.ok) {
    throw new Error(
      `[silo-vaults] Network error: ${response.status} - ${response.statusText}`,
    )
  }
  const payload: any = await response.json()
  if (payload?.errors?.length) {
    throw new Error(
      `[silo-vaults] GraphQL error: ${payload.errors
        .map((e: any) => e?.message)
        .join('; ')}`,
    )
  }

  const items: ApiVaultItem[] = payload?.data?.vaults?.items ?? []

  // Join tables for exposures: allocation positions grouped by vault, and
  // market metadata (collateral, supply APR, share/asset ratio) by id.
  const positions: ApiPositionItem[] = payload?.data?.positions?.items ?? []
  const positionsByVault = new Map<string, ApiPositionItem[]>()
  for (const row of positions) {
    const accountId = (row.accountId ?? '').toLowerCase()
    if (!accountId) continue
    const list = positionsByVault.get(accountId)
    if (list) list.push(row)
    else positionsByVault.set(accountId, [row])
  }

  const markets: ApiMarketItem[] = payload?.data?.markets?.items ?? []
  const marketMeta = new Map<string, MarketMeta>()
  for (const m of markets) {
    const id = (m.id ?? '').toLowerCase()
    if (!id) continue
    const apr = Number(m.depositRate ?? 0)
    marketMeta.set(id, {
      collateralAddress: (m.otherMarket?.inputToken?.id ?? '').toLowerCase(),
      supplyApr: Number.isFinite(apr) ? apr : 0,
      collateralShares: Number(m.collateralSharesSupply ?? 0) || 0,
      collateralAssets: Number(m.collateralAssetsSupply ?? 0) || 0,
      liquidity: Number(m.liquidity ?? 0) || 0,
      utilization: Number(m.utilization ?? 0) || 0,
    })
  }

  // Incentive programs, grouped by the token they pay holders of. Only the
  // ones whose share token IS a vault belong here — the rest pay silo
  // depositors and are the lending side's business.
  const programs: ApiIncentivesProgramItem[] =
    payload?.data?.incentivesPrograms?.items ?? []
  const programsByShareToken = new Map<string, ApiIncentivesProgramItem[]>()
  for (const p of programs) {
    const shareTokenId = (p.shareTokenId ?? '').toLowerCase()
    if (!shareTokenId) continue
    const list = programsByShareToken.get(shareTokenId)
    if (list) list.push(p)
    else programsByShareToken.set(shareTokenId, [p])
  }

  // Reward-token pricing reuses the underlying's resolution order — the
  // token list's `assetGroup` key first, then a chain-scoped address key,
  // then the bare address — so a reward token already priced by some
  // lender's oracle is found under whichever key that oracle published.
  const priceOf = (address: string): number => {
    if (!address) return 0
    const meta = tokenList[address]
    const oracleKey =
      toOracleKey(meta?.assetGroup) ?? toGenericPriceKey(address, chainId)
    return prices[oracleKey] ?? prices[address] ?? 0
  }
  const nowSeconds = Math.floor(Date.now() / 1000)

  const out: SiloVaults = {}
  for (const v of items) {
    if (
      options?.protocolVersion &&
      v.protocol?.protocolVersion !== options.protocolVersion
    ) {
      continue
    }
    const vaultId = (v.id ?? '').toLowerCase()
    const totalAssetsFormatted = Number(v.totalAssets ?? 0) || 0
    const apiTotalAssetsUsd = Number(v.totalAssetsUsd ?? 0) || 0
    // Per-unit USD basis: prefer the vault's own USD valuation, fall back to
    // the supplied price map keyed by underlying address.
    const pricePerUnit =
      totalAssetsFormatted > 0 && apiTotalAssetsUsd > 0
        ? apiTotalAssetsUsd / totalAssetsFormatted
        : (prices[(v.asset?.id ?? '').toLowerCase()] ?? 0)
    const exposures = buildExposures(
      vaultId,
      totalAssetsFormatted,
      pricePerUnit,
      positionsByVault,
      marketMeta,
      tokenList,
    )
    const liquidity = computeSiloLiquidityFormatted(
      vaultId,
      totalAssetsFormatted,
      positionsByVault,
      marketMeta,
    )
    const utilization = computeSiloUtilization(
      vaultId,
      totalAssetsFormatted,
      positionsByVault,
      marketMeta,
    )
    const {
      rewards,
      rewardsRate,
      priced: rewardsPriced,
    } = buildVaultRewards(
      vaultId,
      apiTotalAssetsUsd || totalAssetsFormatted * pricePerUnit,
      programsByShareToken,
      priceOf,
      nowSeconds,
    )
    const parsed = parseVault(v, chainId, prices, tokenList, {
      exposures,
      liquidityFormatted: liquidity.formatted,
      liquidityProven: liquidity.proven,
      utilization,
      rewards,
      rewardsRate,
      rewardsPriced,
    })
    if (parsed) out[parsed.address] = parsed
  }
  return out
}
